import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, CheckCircle2, Clock, Utensils, Package, Truck, XCircle, ArrowLeft } from "lucide-react";
import api from "../api";
import { useApp } from "../AppContext";
import DominicanFlag from "../components/DominicanFlag";
import DevBranding from "../components/DevBranding";

const STEPS = [
    { key: "pending_confirmation", icon: Clock, color: "#C85A17" },
    { key: "confirmed", icon: Clock, color: "#A04711" },
    { key: "preparing", icon: Utensils, color: "#D87033" },
    { key: "ready", icon: Package, color: "#4A7C59" },
    { key: "delivered", icon: Truck, color: "#2C352B" },
];

const OrderTracking = () => {
    const { t, lang, fmtMoney } = useApp();
    const { id: paramId } = useParams();
    const navigate = useNavigate();
    const [search, setSearch] = useState(paramId || "");
    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const fetchOrder = async (id) => {
        if (!id) return;
        setLoading(true);
        setError(null);
        try {
            const r = await api.get(`/orders/track/${id.trim()}`);
            setOrder(r.data);
        } catch (e) {
            setOrder(null);
            setError(t.tracking.notFound);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (paramId) fetchOrder(paramId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paramId]);

    const submit = (e) => {
        e.preventDefault();
        if (search.trim()) navigate(`/track/${search.trim()}`);
    };

    const isCancelled = order?.status === "cancelled";
    const currentIdx = order ? STEPS.findIndex((s) => s.key === order.status) : -1;

    return (
        <div data-testid="tracking-page" className="min-h-screen bg-earth-50 grain">
            <div className="absolute top-0 left-0 right-0 h-1 flex">
                <div className="flex-1" style={{ background: "#002D62" }} />
                <div className="flex-1 bg-white" />
                <div className="flex-1" style={{ background: "#CE1126" }} />
            </div>

            <div className="max-w-3xl mx-auto px-6 py-16 sm:py-24">
                <Link
                    to="/"
                    data-testid="tracking-back-btn"
                    className="inline-flex items-center gap-2 text-sm text-earth-600 hover:text-terracotta-600 mb-8"
                >
                    <ArrowLeft className="w-4 h-4" /> {lang === "es" ? "Inicio" : "Home"}
                </Link>

                <div className="flex items-center gap-3 mb-3">
                    <DominicanFlag className="w-7 h-5 rounded-sm shadow" />
                    <span className="text-xs uppercase tracking-[0.2em] font-bold text-terracotta-600">
                        Delicias Dominicanas
                    </span>
                </div>
                <h1 className="font-serif font-black text-4xl sm:text-5xl text-earth-900 tracking-tight">
                    {t.tracking.title}
                </h1>

                <form onSubmit={submit} className="mt-8 flex gap-3" data-testid="tracking-form">
                    <input
                        data-testid="tracking-input"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder={t.tracking.placeholder}
                        className="flex-1 bg-white border border-earth-200 rounded-full px-5 py-3 outline-none focus:border-terracotta-500"
                    />
                    <button
                        type="submit"
                        data-testid="tracking-search-btn"
                        className="btn-primary"
                    >
                        <Search className="w-4 h-4" /> {t.tracking.track}
                    </button>
                </form>

                {loading && <div className="mt-8 text-earth-600">{t.common.loading}</div>}

                {error && !loading && (
                    <div data-testid="tracking-not-found" className="mt-8 bg-white border border-earth-200 rounded-3xl p-8 text-center text-earth-700">
                        <XCircle className="w-10 h-10 mx-auto text-terracotta-600 mb-3" />
                        {error}
                    </div>
                )}

                {order && !loading && (
                    <motion.div
                        data-testid="tracking-result"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                        className="mt-10 bg-white rounded-[32px] border border-earth-200 shadow-lg overflow-hidden"
                    >
                        <div className="px-7 py-6 border-b border-earth-100 flex flex-wrap items-center justify-between gap-3">
                            <div>
                                <div className="text-xs uppercase tracking-wider font-bold text-earth-500">
                                    {lang === "es" ? "Pedido" : "Order"} #{order.id.slice(0, 8).toUpperCase()}
                                </div>
                                <div className="font-serif text-2xl font-bold text-earth-900 mt-1">
                                    {order.customer_name}
                                </div>
                                <div className="text-xs text-earth-500 mt-1">
                                    {new Date(order.created_at).toLocaleString(
                                        lang === "es" ? "es-DO" : "en-US"
                                    )}
                                </div>
                            </div>
                            <div className="text-right">
                                <div className="text-xs uppercase tracking-wider text-earth-500 font-semibold">
                                    Total
                                </div>
                                <div className="font-serif font-black text-3xl text-terracotta-600">
                                    {fmtMoney(order.total)}
                                </div>
                            </div>
                        </div>

                        {isCancelled ? (
                            <div className="px-7 py-10 bg-earth-100 text-center">
                                <XCircle className="w-12 h-12 mx-auto text-earth-500 mb-3" />
                                <div className="font-serif font-bold text-2xl text-earth-900">
                                    {t.tracking.steps.cancelled}
                                </div>
                            </div>
                        ) : (
                            <div className="px-7 py-8">
                                <ol className="relative">
                                    {STEPS.map((step, i) => {
                                        const done = i <= currentIdx;
                                        const Icon = done ? CheckCircle2 : step.icon;
                                        return (
                                            <li
                                                key={step.key}
                                                data-testid={`tracking-step-${step.key}`}
                                                className="flex gap-4 pb-7 last:pb-0 relative"
                                            >
                                                {i < STEPS.length - 1 && (
                                                    <div
                                                        className={`absolute left-[19px] top-10 bottom-0 w-0.5 ${
                                                            i < currentIdx ? "bg-terracotta-500" : "bg-earth-200"
                                                        }`}
                                                    />
                                                )}
                                                <div
                                                    className={`relative z-10 w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${
                                                        done
                                                            ? "bg-terracotta-500 text-white"
                                                            : "bg-earth-100 text-earth-400"
                                                    }`}
                                                >
                                                    <Icon className="w-5 h-5" />
                                                </div>
                                                <div className="pt-1.5">
                                                    <div
                                                        className={`font-serif font-bold text-lg ${
                                                            done ? "text-earth-900" : "text-earth-500"
                                                        }`}
                                                    >
                                                        {t.tracking.steps[step.key]}
                                                    </div>
                                                    {i === currentIdx && (
                                                        <div className="text-xs text-terracotta-600 font-bold uppercase tracking-wider mt-0.5">
                                                            {lang === "es" ? "En curso" : "In progress"}
                                                        </div>
                                                    )}
                                                </div>
                                            </li>
                                        );
                                    })}
                                </ol>
                            </div>
                        )}

                        <div className="px-7 py-5 bg-earth-50 border-t border-earth-100">
                            <div className="text-xs uppercase tracking-wider font-bold text-earth-500 mb-2">
                                {lang === "es" ? "Tu pedido" : "Your order"}
                            </div>
                            <ul className="text-sm text-earth-800 space-y-1">
                                {order.items.map((it, i) => (
                                    <li key={i} className="flex justify-between">
                                        <span>{it.quantity}× {it.name}</span>
                                        <span className="font-semibold">{fmtMoney(it.price * it.quantity)}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </motion.div>
                )}
            </div>
            <DevBranding variant="page" />
        </div>
    );
};

export default OrderTracking;
